import { useStyletron } from "baseui";
import { LoadingView } from "../../components/LoadingView";
import { ParagraphXSmall } from "baseui/typography";
import { StyledLink } from "baseui/link";
import TimeAgo from "react-timeago";
import { FormattedMessage } from "react-intl";
import { ICompany } from "@oqdo/automationapps/Agents/Organizations/Company";
import { ISubscription, SubscriptionStatus } from "@oqdo/automationapps/Memberships/Subscription";
import { VscArrowRight, VscKey, VscOrganization } from "react-icons/vsc";
import { PiCurrencyCircleDollarLight } from "react-icons/pi";
import { StyledHorizontalContent, StyledSingleLineText, StyledVerticalContent } from "./styled-components";
import { ListItem, ListItemLabel } from "baseui/list";
import { AiOutlineUserAdd, AiOutlineUserDelete } from "react-icons/ai";
import { EmptyState } from "../../components/EmptyState";
import { useMemo } from "react";
import { usePagedEventQueries } from "../../hooks/usePagedEventQueries";
import { LoadNextPageButton } from "../../components/LoadNextPageButton";
import { IBacnetDevice } from "@oqdo/automationapps/Devices/BacnetDevice";
import HierarchyObjectNavigation from "../../components/HierarchyNavigation/HierarchyObjectNavigation";
import { useGetTenantByIdQuery } from "../../services/resourceGroups/tenantApi";
import { useGetResourceGroupByIdQuery } from "../../services/resourceGroups/resourceGroupApi";
import { CgServer } from "react-icons/cg";
import { Badge } from "baseui/badge";

const SubscriptionEvents: React.FC<{ id: string }> = ({ id }) => {
  const [css, theme] = useStyletron();
  const { data, isLoading, isSuccess, error, hasMore, page, setPage, total, isFetching } = usePagedEventQueries(id, 20);

  const sorted = useMemo(
    () => data?.slice().sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime()) ?? [],
    [data]
  );
  return (
    <LoadingView isLoading={isLoading} error={error} isSucess={isSuccess}>
      {(!data || data.length === 0) && <EmptyState header={"No Logs"} />}
      <ul
        className={css({
          padding: 0,
          paddingRight: 0,
          overflowY: "auto",
        })}>
        {sorted?.map((v, i) => (
          <ListItem
            artwork={() => getTypeIcon(v.type)}
            key={i}
            endEnhancer={() => (
              <ParagraphXSmall margin={0}>
                <TimeAgo date={v.timeStamp} />
              </ParagraphXSmall>
            )}>
            <ListItemLabel description={<DataTemplate1 {...v} />}>{v.type?.replace("Data", "")}</ListItemLabel>
          </ListItem>
        ))}
      </ul>
      <LoadNextPageButton hasMore={hasMore} isFetching={isFetching} page={page} total={total} setPage={setPage} />
    </LoadingView>
  );
};

export default SubscriptionEvents;

const getTypeIcon = (type: string) => {
  switch (type) {
    case "StripeItemUpdated":
      return <PiCurrencyCircleDollarLight size={"1.5rem"} />;
    case "UserCreatedData":
    case "UserAddedToTenantData":
      return <AiOutlineUserAdd size={"1.5rem"} />;
    case "UserRemovedFromTenantData":
      return <AiOutlineUserDelete size={"1.5rem"} />;
    case "BacnetDeviceUpdatedData":
    case "BacnetDeviceDeletedData":
      return <CgServer size={"1.5rem"} />;
    case "TenantCreatedData":
    case "TenantMovedData":
      return <VscOrganization size={"1.5rem"} />;
    case "SubscriptionUpdatedData":
      return <VscKey size={"1.5rem"} />;
    default:
      return <></>;
  }
};

const DataTemplate1: React.FC<any> = (props) => {
  switch (props.type) {
    case "UserAddedToTenantData":
      return <UserInfo {...props} />;
    case "UserCreatedData":
      return <UserInfo {...props} />;
    case "UserInvitedData":
      return <UserInvited {...props.data} />;
    case "UserDisabledData":
      return <UserInfo {...props} />;
    case "UserEnabledData":
      return <UserInfo {...props} />;
    case "ResourceGroupMovedData":
      return <ResourceGroupMoved {...props} />;
    case "TenantMovedData":
      return <TenantMoved {...props} />;
    case "SubscriptionUpdatedData":
      return <SubscriptionUpdated {...props} />;
    case "ResourceGroupCreatedData":
      return <ResourceGroupCreated {...props} />;
    case "ResourceGroupDeletedData":
      return <ResourceGroupCreated {...props.data} />;
    case "TenantCreatedData":
      return <TenantCreated {...props} />;
    case "SubscriptionCreatedData":
      return <SubscriptionCreated {...props.data} />;
    case "StripeItemUpdated":
      return <StripeItemUpdated {...props.data} />;
    case "BacnetDeviceUpdatedData":
      return <BacnetDeviceUpdated {...props.data} />;
    case "BacnetDeviceDeletedData":
      return <BacnetDeviceDeleted {...props} />;
    case "UserRemovedFromTenantData":
      return <UserInfo {...props} />;
    default:
      return <>{JSON.stringify(props.data)}</>;
  }
};

const BacnetDeviceDeleted: React.FC<{
  subscriptionId: string;
  resourceGroupId: string;
  bacnetDeviceId: string;
  data: { device: IBacnetDevice };
}> = ({ bacnetDeviceId, data, subscriptionId, resourceGroupId }) => {
  const { data: rg } = useGetResourceGroupByIdQuery(resourceGroupId);
  const devName = data.device.description !== "" ? data.device.description : data.device.name;
  return (
    <StyledVerticalContent>
      <StyledLink href={`/subscriptions/${subscriptionId}/resourceGroups/${resourceGroupId}`}>{rg?.name}</StyledLink>
      {devName}
      <StyledSingleLineText>
        DeviceID <Badge content={data.device.deviceId} color="primary" hierarchy="secondary"></Badge>
      </StyledSingleLineText>
    </StyledVerticalContent>
  );
};

const BacnetDeviceUpdated: React.FC<{
  bacnetDeviceId: string;
  patch: any;
  device: IBacnetDevice;
}> = ({ bacnetDeviceId, patch, device }) => {
  const [css, theme] = useStyletron();

  const changedProperties = patch.patch
    .map((p) => ({ path: p.path.replace("/", ""), value: p.value }))
    .find((p) => p.path === "TotalObjects");

  return (
    <StyledVerticalContent>
      <HierarchyObjectNavigation {...device}></HierarchyObjectNavigation>
      <StyledSingleLineText>
        DeviceID <Badge content={device.deviceId} color="primary" hierarchy="secondary"></Badge>
      </StyledSingleLineText>
      <StyledSingleLineText>
        Objects <Badge content={changedProperties?.value} color="primary" hierarchy="secondary"></Badge>
      </StyledSingleLineText>
    </StyledVerticalContent>
  );
};

const UserInfo: React.FC<{
  subscriptionId: string;
  tenantId: string;
  data: { user: { displayName: string; mail: string } };
}> = (props) => {
  const user = props.data.user;
  const { data: tenant } = useGetTenantByIdQuery(props.tenantId);
  return (
    <StyledHorizontalContent>
      <StyledSingleLineText>
        {user.displayName} ({user.mail})
      </StyledSingleLineText>
      <StyledLink href={`/subscriptions/${props.subscriptionId}/tenants/${props.tenantId}`}>
        {tenant ? tenant.description : <FormattedMessage id="Tenant" defaultMessage={"Tenant"} />}
      </StyledLink>
    </StyledHorizontalContent>
  );
};

const StripeItemUpdated: React.FC<{
  stripe: { name: string };
  newQuantity: number;
  oldQuantity: number;
  oqdoProductId: string;
}> = (props) => {
  return (
    <StyledSingleLineText>
      {props.oqdoProductId}: {props.oldQuantity} <VscArrowRight /> {props.newQuantity}
    </StyledSingleLineText>
  );
};

const TenantCreated: React.FC<{
  tenantId: string;
  subscriptionId: string;
  data: { tenant: { name: string } };
}> = (props) => {
  return (
    <StyledLink href={`/subscriptions/${props.subscriptionId}/tenants/${props.tenantId}`}>
      {props.data.tenant.name}
    </StyledLink>
  );
};

const ResourceGroupCreated: React.FC<{
  resourceGroupId: string;
  subscriptionId: string;
  data: { resourceGroup: { name: string } };
}> = (props) => {
  return (
    <StyledLink href={`/subscriptions/${props.subscriptionId}/resourceGroups/${props.resourceGroupId}`}>
      {props.data.resourceGroup.name}
    </StyledLink>
  );
};

const UserInvited: React.FC<{
  attempt: number;
  user: { displayName: string; mail: string };
}> = (props) => {
  return (
    <StyledSingleLineText>
      {props.user.displayName} Attempt: {props.attempt} ({props.user.mail})
    </StyledSingleLineText>
  );
};

const ResourceGroupMoved: React.FC<{
  resourceGroupId: string;
  data: {
    resourceGroup: { id: string; name: string };
    fromSubscription: { id: string; name: string };
    toSubscription: { id: string; name: string };
  };
}> = (props) => {
  return (
    <>
      <StyledLink href={`/subscriptions/${props.data.toSubscription.id}/tenants/${props.resourceGroupId}`}>
        {props.data.resourceGroup?.name ?? "name unknown"}
      </StyledLink>{" "}
      From Subscription:{" "}
      <StyledLink href={`/subscriptions/${props.data.fromSubscription.id}`}>
        {props.data.fromSubscription.name}
      </StyledLink>{" "}
      to{" "}
      <StyledLink href={`/subscriptions/${props.data.toSubscription.id}`}>{props.data.toSubscription.name} </StyledLink>
    </>
  );
};

const TenantMoved: React.FC<{
  tenantId: string;
  data: {
    tenant: ICompany;
    fromSubscription: ISubscription;
    toSubscription: ISubscription;
  };
}> = (props) => {
  return (
    <>
      <StyledLink href={`/subscriptions/${props.data.toSubscription.id}/tenants/${props.tenantId}`}>
        {props.data.tenant?.name ?? "name unknown"}
      </StyledLink>{" "}
      from{" "}
      <StyledLink href={`/subscriptions/${props.data.fromSubscription.id}`}>
        {props.data.fromSubscription.name}
      </StyledLink>{" "}
      to{" "}
      <StyledLink href={`/subscriptions/${props.data.toSubscription.id}`}>{props.data.toSubscription.name} </StyledLink>
    </>
  );
};

const SubscriptionUpdated: React.FC<{
  data: {
    patch: any;
    subscription: ISubscription;
  };
}> = (props) => {
  const changedProperties = getValueByPath(props.data.patch.patch, "Products");
  const statusChanged = getValueByPath(props.data.patch.patch, "Status");
  console.log("SubscriptionUpdated", changedProperties, statusChanged);
  const text = changedProperties ? JSON.stringify(changedProperties) : "";
  const statusText = statusChanged ? `Status: ${SubscriptionStatus[statusChanged]}` : "";
  return (
    <StyledSingleLineText title={text}>
      {text} {statusText}
    </StyledSingleLineText>
  );
};

const SubscriptionCreated: React.FC<{
  subscription: { name: string };
}> = (props) => {
  return <StyledSingleLineText>{props.subscription.name}</StyledSingleLineText>;
};

type PatchOp = {
  value: any;
  path: string;
  op: string;
};
// Function to get value by path
function getValueByPath(patch: PatchOp[], path: string) {
  for (const op of patch) {
    if (op.path === path || op.path === `/${path}`) {
      return op.value;
    }
    // For root path, check inside value
    if (op.path === "" && op.value && path in op.value) {
      return op.value[path.replace(/^\//, "")];
    }
  }
  return undefined;
}
